import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CalendarPlus,
  Clock,
  Info,
  LifeBuoy,
  Paperclip,
  RotateCcw,
  Star,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getAssociateProfile, isAuthenticated } from '../services/otpAuthService';
import { useSupportSocket } from '../hooks/useSupportSocket';
import { associateSupportApi } from '../services/adapters/requesterSupportApi';
import type { RequesterTicketDetail } from '../services/supportApi';
import StatusBadge from '../components/support/StatusBadge';
import { requesterBadgeLabels } from '../components/support/displayStatus';
import PriorityBadge from '../components/support/PriorityBadge';
import StatusTimeline from '../components/support/requester/StatusTimeline';
import CaseHistory from '../components/support/requester/CaseHistory';
import UpdateComposer from '../components/support/requester/UpdateComposer';
import ConfirmModal from '../components/support/requester/ConfirmModal';
import DatiPraticaCard from '../components/support/requester/DatiPraticaCard';
import {
  canReopenRequester,
  isConcluded,
  reopenDeadline,
  lastMessageIsMine,
  needsRequesterAction,
} from '../components/support/requester/requesterModel';
import {
  ACCEPTED_TYPES,
  MAX_FILES,
  MAX_FILE_SIZE,
  REOPEN_WINDOW_DAYS,
  REQUESTER_STATUS_LABELS,
  formatShortDate,
} from '../components/support/constants';

/**
 * File statici (`/uploads/...`) serviti da Express SENZA il prefisso `/api`:
 * `REACT_APP_API_BASE_URL` lo include, quindi va tolto.
 */
const FILES_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

/** Carta di base: stessa grammatica delle altre pagine dell'area riservata. */
const CARD = 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';
const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';

/** Una frase per stato: che cosa deve aspettarsi il richiedente adesso. */
const NEXT_STEP_TEXT: Record<string, string> = {
  aperto: 'Il canale prenderà in carico la richiesta e ti risponderà qui; riceverai un’email.',
  preso_in_carico: 'Il canale sta lavorando alla tua richiesta: ti avviseremo via email.',
  in_lavorazione: 'Il canale sta lavorando alla tua richiesta: ti avviseremo via email.',
  in_attesa_associato: 'Serve una tua risposta per andare avanti.',
  risolto: `Se il problema persiste puoi riaprire la richiesta entro ${REOPEN_WINDOW_DAYS} giorni.`,
  chiuso: 'Richiesta chiusa. Per un nuovo problema apri una nuova richiesta.',
};

/** Data senza orario: le scadenze della finestra di riapertura si leggono a giorni. */
const formatDay = (iso: string | Date) =>
  new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Riga "Cosa succede ora". Su risolto e chiuso porta le date: la risoluzione con
 * la scadenza della riapertura, la chiusura come fatto definitivo.
 */
const nextStepText = (ticket: { status: string; resolvedAt?: string; closedAt?: string }): string => {
  if (ticket.status === 'risolto' && ticket.resolvedAt) {
    const deadline = reopenDeadline(ticket, REOPEN_WINDOW_DAYS);
    return `Risolta il ${formatDay(ticket.resolvedAt)}${deadline ? `: puoi riaprirla fino al ${formatDay(deadline)}` : ''}.`;
  }
  if (ticket.status === 'chiuso' && ticket.closedAt) {
    return `Richiesta chiusa il ${formatDay(ticket.closedAt)}. Per un nuovo problema apri una nuova richiesta.`;
  }
  return NEXT_STEP_TEXT[ticket.status] || NEXT_STEP_TEXT.in_lavorazione;
};

// Dettaglio della richiesta lato associato: e una PRATICA, non una chat.
// Timeline dello stato, cronologia datata, e il riquadro di scrittura si apre
// solo dal bottone "Aggiungi comunicazione".
const SupportTicketDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const api = associateSupportApi;
  // L'adapter e un singleton di modulo, ma l'effetto di caricamento non deve
  // dipendere dalla sua identita: legge apiRef.current.
  const apiRef = useRef(api);
  apiRef.current = api;

  const [ticket, setTicket] = useState<RequesterTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Foto dell'associato per l'avatar della cronologia. Sta nel profilo, non nel
  // ticket: si legge dallo stesso endpoint della dashboard (`/associate/profile`)
  // invece di allargare `toRequesterView`, cosi il backend resta intatto.
  const [photoPath, setPhotoPath] = useState<string | undefined>();

  // Nuova comunicazione
  const [replyBody, setReplyBody] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);

  // Riapertura
  const [isReopening, setIsReopening] = useState(false);
  const [askReopen, setAskReopen] = useState(false);
  const reopenButtonRef = useRef<HTMLButtonElement>(null);

  // Valutazione
  const [isRating, setIsRating] = useState(false);
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  // Auth check
  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadTicket = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const data = await apiRef.current.getTicket(id);
      setTicket(data);
    } catch (error: any) {
      // 404 (ticket eliminato o inesistente): basta lo stato vuoto «Richiesta
      // non trovata», nessun toast d'errore.
      if (error?.response?.status === 404) setTicket(null);
      else if (!silent) toast.error('Errore nel caricamento del ticket');
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isAuthenticated()) {
      loadTicket();
    }
  }, [loadTicket]);

  // La foto e un ornamento: se il profilo non risponde restano le iniziali.
  useEffect(() => {
    if (!isAuthenticated()) return;
    let cancelled = false;
    getAssociateProfile()
      .then((p) => { if (!cancelled) setPhotoPath(p?.foto || undefined); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // Realtime: ricarica il ticket ad ogni evento sul namespace /support
  useSupportSocket({
    area: 'associate',
    ticketId: id,
    onTicketEvent: () => loadTicket(true),
  });

  const handleFilesSelected = (selected: File[]) => {
    const valid: File[] = [];
    for (const f of selected) {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        toast.error(`Tipo file non ammesso: ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`"${f.name}" supera i 10MB`);
        continue;
      }
      valid.push(f);
    }

    setReplyFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES) {
        toast.error(`Massimo ${MAX_FILES} file`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (index: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetComposer = () => {
    setReplyBody('');
    setReplyFiles([]);
  };

  // Restituisce `true` solo a invio riuscito: e il segnale con cui
  // `UpdateComposer` decide se richiudere il riquadro o lasciarlo da ritentare.
  const handleSend = async (): Promise<boolean> => {
    if (!id || (!replyBody.trim() && replyFiles.length === 0)) {
      toast.error('Scrivi un messaggio o allega un file');
      return false;
    }
    setIsSending(true);
    try {
      await api.reply(id, replyBody.trim(), replyFiles.length > 0 ? replyFiles : undefined);
      resetComposer();
      toast.success('Comunicazione inviata!');
      await loadTicket();
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Errore durante l\'invio');
      return false;
    } finally {
      setIsSending(false);
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    setAskReopen(false);
    setIsReopening(true);
    try {
      await api.reopen(id);
      toast.success('Richiesta riaperta');
      await loadTicket();
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.warning('Finestra di riapertura scaduta');
      } else {
        toast.error(error.response?.data?.error || 'Errore durante la riapertura');
      }
    } finally {
      setIsReopening(false);
    }
  };

  const handleRate = async () => {
    if (!id) return;
    if (stars < 1) {
      toast.error('Seleziona una valutazione');
      return;
    }
    setIsRating(true);
    try {
      await api.rate(id, stars, ratingComment.trim());
      toast.success('Grazie per la tua valutazione!');
      await loadTicket();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Errore durante l\'invio della valutazione');
    } finally {
      setIsRating(false);
    }
  };

  const handleOpenAttachment = async (storedName: string, displayName?: string) => {
    if (!ticket) return;
    try {
      await api.openAttachment(ticket._id, storedName, displayName);
    } catch {
      toast.error('Impossibile aprire l\'allegato');
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Richiesta non trovata</p>
        <button
          onClick={() => navigate('/area-riservata/supporto')}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alle richieste
        </button>
      </div>
    );
  }

  const channelName = ticket.canale || 'UNASCA';
  const concluded = isConcluded(ticket);
  const tocaATe = needsRequesterAction(ticket);
  const canRate = concluded && !ticket.rating;
  const canReopen = canReopenRequester(ticket, REOPEN_WINDOW_DAYS);
  const attachmentCount = (ticket.publicThread || []).reduce(
    (n, m) => n + (m.attachments?.length || 0),
    0
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ===== Colonna principale ===== */}
          <div className="lg:col-span-2 space-y-6">
            {/* Intestazione */}
            <div className={`${CARD} p-5`}>
              <button
                onClick={() => navigate('/area-riservata/supporto')}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Le mie richieste
              </button>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                  #{ticket.ticketNumber}
                </span>
                <span title={requesterBadgeLabels(ticket, REQUESTER_STATUS_LABELS).title}>
                  <StatusBadge status={ticket.status} labels={requesterBadgeLabels(ticket, REQUESTER_STATUS_LABELS).labels} dot />
                </span>
                <PriorityBadge priority={ticket.priority} />
              </div>

              <h1 className="mt-1.5 text-lg font-semibold text-gray-900 dark:text-white">
                {ticket.subject}
              </h1>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <LifeBuoy className="w-3.5 h-3.5" aria-hidden="true" />
                  {channelName}
                </span>
                {ticket.createdAt && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarPlus className="w-3.5 h-3.5" aria-hidden="true" />
                    Aperta il {formatShortDate(ticket.createdAt)}
                  </span>
                )}
                {ticket.lastActivityAt && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    Ultimo aggiornamento {formatShortDate(ticket.lastActivityAt)}
                  </span>
                )}
              </div>
            </div>

            {/* Stato della richiesta */}
            <div className={`${CARD} p-5`}>
              <p className={`${SECTION_LABEL} mb-4`}>Stato della richiesta</p>
              <StatusTimeline ticket={ticket} />
            </div>

            {/* Tocca a te / riga neutra */}
            {tocaATe ? (
              <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 flex items-start gap-3">
                <AlertCircle
                  className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
                  aria-hidden="true"
                />
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  <span className="font-semibold">Tocca a te.</span>{' '}
                  {ticket.status === 'in_attesa_associato'
                    ? 'Il canale attende una tua risposta per andare avanti.'
                    : 'C’è una risposta del canale che non hai ancora letto.'}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 flex items-start gap-3">
                <Info
                  className="w-4 h-4 mt-0.5 shrink-0 text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {concluded
                    ? 'Questa richiesta è conclusa.'
                    : 'Il canale sta lavorando alla tua richiesta: ti avviseremo via email.'}
                </p>
              </div>
            )}

            {/* Cronologia */}
            <div className={`${CARD} p-5`}>
              <p className={`${SECTION_LABEL} mb-4`}>Cronologia della richiesta</p>
              <CaseHistory
                messages={ticket.publicThread}
                mineAuthorType="associate"
                channelName={channelName}
                requesterName={ticket.requester?.nome || ticket.associateSnapshot?.nominativo}
                requesterPhotoUrl={photoPath ? `${FILES_BASE}${photoPath}` : undefined}
                onOpenAttachment={handleOpenAttachment}
              />
              {/* Pratica conclusa (risolta o chiusa): non si aggiungono
                  comunicazioni, si riapre (ruling utente 2026-09-23). */}
              {!concluded && (
                <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
                  <UpdateComposer
                    body={replyBody}
                    onBodyChange={setReplyBody}
                    files={replyFiles}
                    onAddFiles={handleFilesSelected}
                    onRemoveFile={removeFile}
                    onSend={handleSend}
                    onCancel={resetComposer}
                    isSending={isSending}
                    lastMessageIsMine={lastMessageIsMine(ticket.publicThread, 'associate')}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ===== Colonna laterale ===== */}
          <aside className="lg:col-span-1 space-y-6">
            {/* Dettagli */}
            <div className={`${CARD} p-4`}>
              <p className={`${SECTION_LABEL} mb-3`}>Dettagli</p>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Canale</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 text-right">{channelName}</dd>
                </div>
                {/* Stato e priorita stanno gia nell'intestazione: qui
                    sarebbero una seconda copia da tenere allineata. */}
                {ticket.createdAt && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Aperta il</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 text-right tabular-nums">
                      {formatShortDate(ticket.createdAt)}
                    </dd>
                  </div>
                )}
                {ticket.lastActivityAt && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                      Ultimo aggiornamento
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 text-right tabular-nums">
                      {formatShortDate(ticket.lastActivityAt)}
                    </dd>
                  </div>
                )}
                {ticket.firstResponseAt && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Prima risposta</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 text-right tabular-nums">
                      {formatShortDate(ticket.firstResponseAt)}
                    </dd>
                  </div>
                )}
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Allegati</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 text-right inline-flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                    {attachmentCount}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Dati della pratica (canale Documento Unico): solo se ci sono campi o valori */}
            <DatiPraticaCard ticket={ticket} className={`${CARD} p-4`} labelClassName={SECTION_LABEL} />

            {/* Cosa succede ora */}
            <div className={`${CARD} p-4`}>
              <p className={`${SECTION_LABEL} mb-2`}>Cosa succede ora</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {nextStepText(ticket)}
              </p>
            </div>

            {/* Riapertura */}
            {canReopen && (
              <div className={`${CARD} p-4`}>
                <p className={`${SECTION_LABEL} mb-2`}>Il problema non è risolto?</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Puoi riaprire la richiesta entro {REOPEN_WINDOW_DAYS} giorni dalla risoluzione.
                </p>
                <button
                  ref={reopenButtonRef}
                  onClick={() => setAskReopen(true)}
                  disabled={isReopening}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-indigo-200 dark:border-indigo-700 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReopening ? (
                    <>
                      <span className="w-4 h-4 border-2 border-indigo-400/40 border-t-indigo-600 rounded-full animate-spin" />
                      Riapertura…
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Riapri richiesta
                    </>
                  )}
                </button>

                <ConfirmModal
                  open={askReopen}
                  title="Riaprire la richiesta?"
                  message="La richiesta risulta risolta. Riaprila solo se il problema non è stato effettivamente risolto o se è emersa una necessità urgente collegata: il canale la riprenderà in carico e ti risponderà qui. Per una richiesta nuova o diversa, apri una nuova richiesta."
                  confirmLabel="Riapri la richiesta"
                  onConfirm={handleReopen}
                  onCancel={() => setAskReopen(false)}
                  returnRef={reopenButtonRef}
                  busy={isReopening}
                />
              </div>
            )}

            {/* Valutazione */}
            {canRate && (
              <div className={`${CARD} p-4`}>
                <p className={`${SECTION_LABEL} mb-2`}>Valuta l’assistenza ricevuta</p>
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStars(s)}
                      onMouseEnter={() => setHoverStars(s)}
                      onMouseLeave={() => setHoverStars(0)}
                      className="p-0.5"
                      aria-label={`${s} stelle`}
                    >
                      <Star
                        className={`w-5 h-5 transition-colors ${
                          s <= (hoverStars || stars)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder="Lascia un commento (facoltativo)..."
                  rows={3}
                  maxLength={1000}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
                <button
                  onClick={handleRate}
                  disabled={isRating || stars < 1}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRating ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Invio…
                    </>
                  ) : (
                    'Invia valutazione'
                  )}
                </button>
              </div>
            )}

            {/* Valutazione gia inviata */}
            {ticket.rating && (
              <div className={`${CARD} p-4`}>
                <p className={`${SECTION_LABEL} mb-2`}>La tua valutazione</p>
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      aria-hidden="true"
                      className={`w-4 h-4 ${
                        s <= ticket.rating!.stars
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  ))}
                  <span className="sr-only">{ticket.rating.stars} stelle su 5</span>
                </div>
                {ticket.rating.comment && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {ticket.rating.comment}
                  </p>
                )}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetailPage;
